// Desktop filter modal — opened by the sliders icon inside the
// PillSearchBar. Centered dialog, body-scroll-lock, Escape-to-close.
//
// Four sections in a single scroll:
//   Oraș       — single-select dropdown
//   Categorie  — CategoryGrid (same 13 chips as the page strip)
//   Preț       — range slider
//   Rating     — chip buttons
//
// Categorie writes through the shared categories helper so the
// chip selection here behaves identically to a click in the page
// strip — same URL params, same active resolution. The other three
// stay as draft state because they map to filter values, not
// navigation state, and we want one explicit "Aplică" commit.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import CategoryGrid from './CategoryGrid';
import {
  CATEGORIES,
  getActiveCategoryId,
  commitCategoryToUrl,
} from './categories';
import LocationPopover, { LOCATION_POPOVER_OPTION_COUNT } from './LocationPopover';
import useComboboxNav from '../../hooks/useComboboxNav';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import api from '../../utils/api';

export default function FilterModal({
  open,
  onClose,
  priceRange,
  setPriceRange,
  minRating,
  setMinRating,
  onApply,
}) {
  const overlayRef = useRef(null);
  // Lift the modal above the keyboard in sync with it (no native-resize lag).
  useKeyboardInset(overlayRef, open);
  const [searchParams, setSearchParams] = useSearchParams();

  // Draft category id + city, lifted from the URL when the modal opens.
  // We don't write back until Apply so a visitor can experiment with chips
  // and type a city without the page underneath refetching on every
  // keystroke/click.
  const [draftCategoryId, setDraftCategoryId] = useState('all');
  const [draftCity, setDraftCity] = useState('');

  // Price + rating drafts — mirror the category/city draft model so a slider
  // drag or rating tap doesn't commit to the parent (and refire the page's
  // un-debounced /salons fetch) on every tick. They're committed to the parent
  // only on Aplică. Initialised from the parent's committed values on open.
  const [draftPriceRange, setDraftPriceRange] = useState(priceRange);
  const [draftMinRating, setDraftMinRating] = useState(minRating);

  // City suggestion popover state — same Airbnb-style flyout used
  // by the page pill. Opens on input focus, closes on outside
  // click / Escape / picking a destination.
  const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
  const cityFieldRef = useRef(null);
  const popoverRef = useRef(null);

  // Combobox keyboard nav — see PillSearchBar's matching hook for
  // the full description. Escape closes the popover; the modal's
  // global Escape handler (further down) still catches the same
  // key when the popover is already closed.
  const { activeIndex, activeId, onKeyDown: onComboKeyDown, setActiveIndex } =
    useComboboxNav({
      isOpen: cityPopoverOpen,
      optionCount: LOCATION_POPOVER_OPTION_COUNT,
      listboxId: 'filter-modal-city-listbox',
      onActivate: (idx) => popoverRef.current?.activate(idx),
      onClose: () => setCityPopoverOpen(false),
    });

  useEffect(() => {
    if (!cityPopoverOpen) return;
    const onDocClick = (e) => {
      if (cityFieldRef.current && !cityFieldRef.current.contains(e.target)) {
        setCityPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [cityPopoverOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Initialise ALL drafts (category, city, price, rating) from the committed
  // state on the open transition ONLY. Keying this on [open] — not searchParams —
  // means a URL change while the modal is open can't clobber the user's
  // in-progress drafts back to the committed values mid-edit.
  useEffect(() => {
    if (!open) return;
    setDraftCategoryId(getActiveCategoryId(searchParams));
    setDraftCity(searchParams.get('city') || '');
    setDraftPriceRange(priceRange);
    setDraftMinRating(minRating);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Live result count for the current draft. Hits the same /salons
  // endpoint the page uses, but with the in-modal draft (category +
  // city) overlaid on the already-live price/rating/query, so the
  // button previews what Apply will actually surface — not the count
  // for whatever filters are still committed to the URL. Debounced so
  // dragging the price slider doesn't fire a request per tick.
  const [draftCount, setDraftCount] = useState(null);
  // Depend on the price VALUES, not the array reference — clearing filters
  // re-creates [0, 500] with the same numbers, which would otherwise re-fire
  // the count request for no actual change. Read the DRAFT range so the live
  // count previews what Aplică will surface as the user drags.
  const [minPrice, maxPrice] = draftPriceRange;
  useEffect(() => {
    // Clear any previous count up front. On close it just resets; while the
    // modal is open it reverts the button to "Aplica filtrele" instead of
    // showing a stale number during the 300ms debounce + request window.
    setDraftCount(null);
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const entry = CATEGORIES.find((c) => c.id === draftCategoryId);
      const params = { limit: 1 };
      if (entry?.species) params.species = entry.species;
      else if (entry?.marketplaceTagSlug) params.marketplaceTag = entry.marketplaceTagSlug;
      const city = draftCity.trim();
      if (city) params.city = city;
      if (draftMinRating > 0) params.rating = draftMinRating;
      if (minPrice > 0) params.minPrice = minPrice;
      if (maxPrice < 500) params.maxPrice = maxPrice;
      const q = searchParams.get('q');
      if (q) params.query = q;
      try {
        const res = await api.get('/salons', { params });
        if (!cancelled) setDraftCount(Number(res.data?.pagination?.total ?? 0));
      } catch {
        if (!cancelled) setDraftCount(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, draftCategoryId, draftCity, draftMinRating, minPrice, maxPrice, searchParams]);

  if (!open) return null;

  const handleApply = () => {
    // Commit the price + rating drafts to the parent now (one refetch), instead
    // of on every slider tick. Batched with the searchParams update below into a
    // single render → a single /salons fetch.
    setPriceRange(draftPriceRange);
    setMinRating(draftMinRating);
    const entry = CATEGORIES.find((c) => c.id === draftCategoryId);
    // Commit category + city in one URL update so they don't clobber each
    // other (two setSearchParams in a tick would each read the committed
    // params, not the queued ones). commitCategoryToUrl returns a fresh
    // clone, so layering the city mutation on its result leaves React
    // Router's memoized `prev` untouched.
    setSearchParams((prev) => {
      const next = commitCategoryToUrl(prev, entry?.id === 'all' ? null : entry);
      const city = draftCity.trim();
      if (city) next.set('city', city);
      else next.delete('city');
      return next;
    });
    onApply?.();
    onClose();
  };

  // "Șterge tot" resets the DRAFTS only — like every other control in this
  // modal, the clear isn't committed until Aplică. So closing without Aplică
  // keeps the existing filters, and the search query (q, owned by the search
  // bar, not this filter modal) is never collaterally cleared.
  const handleClear = () => {
    setDraftCategoryId('all');
    setDraftCity('');
    setDraftPriceRange([0, 500]);
    setDraftMinRating(0);
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-[90] flex flex-col bg-cream-100 sm:flex-row sm:items-center sm:justify-center sm:bg-black/50 sm:p-4"
      style={{ transition: 'padding-bottom 0.25s ease-out' }}
      role="dialog"
      aria-modal="true"
      aria-label="Toate filtrele"
    >
      <div className="flex w-full flex-1 flex-col overflow-hidden bg-cream-50 sm:max-h-[90vh] sm:max-w-xl sm:flex-none sm:rounded-2xl sm:shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink/10 px-6 pb-4 pt-[max(env(safe-area-inset-top,0px),1rem)] sm:pt-4">
          <h2 className="font-display text-lg font-semibold text-ink">Toate filtrele</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            className="rounded-lg p-2 text-ink-400 transition-colors hover:bg-ink/5"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
          {/* City — free-text input with the same Airbnb-style
              LocationPopover the page pill uses. Typing updates the local
              draftCity only; the ?city= param (and thus the page refetch) is
              written on Aplică, so no request fires per keystroke. */}
          <div>
            <label
              htmlFor="filter-modal-city"
              className="mb-2 block text-sm font-medium text-ink-600"
            >
              Oraș
            </label>
            <div ref={cityFieldRef} className="relative">
              <input
                id="filter-modal-city"
                type="text"
                value={draftCity}
                onFocus={() => setCityPopoverOpen(true)}
                onChange={(e) => {
                  setDraftCity(e.target.value);
                  // Typing invalidates the previous keyboard highlight
                  // so a stale ArrowDown doesn't override the typed
                  // value when the visitor presses Enter or Apply.
                  setActiveIndex(-1);
                }}
                onKeyDown={onComboKeyDown}
                placeholder="Toate orașele"
                // ARIA combobox pattern: input role + listbox link.
                // aria-controls points at the listbox's id (only while
                // it's rendered — see PR #681); each row carries
                // role="option" so screen readers announce them as
                // selectable items. aria-activedescendant tracks the
                // currently-highlighted row without moving DOM focus
                // away from the textbox.
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cityPopoverOpen}
                aria-haspopup="listbox"
                aria-controls={cityPopoverOpen ? 'filter-modal-city-listbox' : undefined}
                aria-activedescendant={activeId}
                autoComplete="off"
                className="focus-ring w-full rounded-xl border border-ink/15 bg-cream-50 px-3 py-2.5 text-sm text-ink placeholder-ink-400"
              />
              {cityPopoverOpen && (
                <LocationPopover
                  ref={popoverRef}
                  id="filter-modal-city-listbox"
                  activeIndex={activeIndex}
                  onActiveIndexChange={setActiveIndex}
                  onPickCity={(name) => {
                    setDraftCity(name);
                    setCityPopoverOpen(false);
                  }}
                  className="absolute left-0 top-full z-10 mt-2 w-full"
                />
              )}
            </div>
          </div>

          {/* Category grid — same 13 chips as the page strip */}
          <div>
            <label className="mb-3 block text-sm font-medium text-ink-600">Categorie</label>
            <CategoryGrid value={draftCategoryId} onChange={(entry) => setDraftCategoryId(entry.id)} />
          </div>

          {/* Price range */}
          <div>
            <label className="mb-3 block text-sm font-medium text-ink-600">
              Preț: {draftPriceRange[0]} - {draftPriceRange[1]} RON
            </label>
            <input
              type="range"
              min="0"
              max="500"
              step="10"
              value={draftPriceRange[1]}
              onChange={(e) => setDraftPriceRange([draftPriceRange[0], parseInt(e.target.value, 10)])}
              className="w-full accent-primary-500"
            />
          </div>

          {/* Rating */}
          <div>
            <label className="mb-3 block text-sm font-medium text-ink-600">Rating minim</label>
            <div className="flex flex-wrap gap-2">
              {[0, 3, 3.5, 4, 4.5].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setDraftMinRating(r)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    draftMinRating === r ? 'bg-ink text-cream-50' : 'bg-ink/5 text-ink-600 hover:bg-ink/10'
                  }`}
                >
                  {r === 0 ? 'Toate' : `${r}+`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-ink/10 bg-cream-50 px-6 pt-4 pb-[max(env(safe-area-inset-bottom,0px),1rem)] sm:pb-4">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm font-medium text-ink-600 underline-offset-4 hover:underline"
          >
            Șterge tot
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="rounded-xl bg-ink px-5 py-2.5 text-sm font-medium text-cream-50 shadow-sm transition-colors hover:bg-ink-700"
          >
            {draftCount == null
              ? 'Aplică filtrele'
              : `Afișează ${draftCount} ${draftCount === 1 ? 'rezultat' : 'rezultate'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
